import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useIsLBUser from '../../utils/useIsLBUser';
import useOnboardingDialog from '../../utils/useOnboardingDialog';
import useRouter from '../../utils/useRouter';
import {
  lbReOnboardingDialog,
  lbReOnboardingAnchorKey,
  LB_REONBOARDING_GAP_MS,
  routes,
} from '../../constants';
import { SnackbarContext } from '../../state/SnackbarContextProvider';
import ReOnboardingDialog, { ReOnboardingStep } from './ReOnboardingDialog';
import { safeLocalStorage } from '../../utils/safeStorage';

const STEPS: ReOnboardingStep[] = [
  'schulfaecher',
  'wichtigkeit',
  'roles',
  'schule',
  'bundesland',
  'schulstufe',
];

const hasReOnboardingGapElapsed = (): boolean => {
  const anchor = safeLocalStorage.getItem(lbReOnboardingAnchorKey);
  const ts = Number(anchor);
  if (!anchor || !Number.isFinite(ts)) return false;
  return Date.now() - ts >= LB_REONBOARDING_GAP_MS;
};

const LBReOnboardingDialog: React.FC = () => {
  const { t } = useTranslation();
  const isLBUser = useIsLBUser();
  const { isShown, markDialogAsSeen } = useOnboardingDialog(lbReOnboardingDialog);
  const { openSnackbar } = useContext(SnackbarContext);
  const { match } = useRouter();

  const isOnDashboard = useMemo(
    () => match.location.pathname.startsWith(`/${routes.dashboard.root}`),
    [match.location.pathname]
  );

  // Seed gap anchor on first mount for LB user (replaces previous anchor from Welcome dialog dismissal).
  useEffect(() => {
    if (isLBUser && !safeLocalStorage.getItem(lbReOnboardingAnchorKey)) {
      safeLocalStorage.setItem(lbReOnboardingAnchorKey, String(Date.now()));
    }
  }, [isLBUser]);

  const shouldShow = isLBUser
    && isOnDashboard
    && !isShown
    && hasReOnboardingGapElapsed();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (shouldShow && !isOpen) {
      setIsOpen(true);
    }
  }, [shouldShow]);

  if (!shouldShow && !isOpen) return null;

  const handleClose = () => {
    markDialogAsSeen();
    setIsOpen(false);
  };

  const handleComplete = () => {
    handleClose();
    openSnackbar({
      type: 'success',
      message: t('Your profile has been updated successfully!'),
    });
  };

  return (
    <ReOnboardingDialog
      open={isOpen}
      steps={STEPS}
      onComplete={handleComplete}
      onClose={handleClose}
      dataTestId='lb-reonboarding-dialog'
    />
  );
};

export default LBReOnboardingDialog;
